import React, { useEffect, useState } from 'react';
import { MapPin, AlertTriangle, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, errMsg } from '../api';
import { Card, goldBtn, ghostBtn } from '../ui';
import CityInput from './CityInput';
import { StageFields, emptyStage, field } from './StageFields';
import { ArrivalFields } from './FlightFields';

const STATE = { confirmed: ['Arrivo confermato', '#2E9E6A'], pending: ['In attesa di conferma', '#D4AF37'], missed: ['Arrivo NON confermato', '#E5484D'] };
const hhmm = (iso, tz) => new Date(iso).toLocaleString('it-IT', { timeZone: tz || 'Europe/Rome', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

function ArrivalInfoForm({ trip, s, first, onSaved }) {
  const [v, setV] = useState({ arrival_time: s.arrival_time || '', flight_no: s.flight_no || '', departure_time: s.departure_time || '' });
  const save = async (e) => {
    e.preventDefault();
    try { await api.put(`/travel/trips/${trip.id}/stages/${s.id}/arrival-info`, { arrival_time: v.arrival_time, flight_no: v.flight_no || null, departure_time: first && v.flight_no && v.departure_time ? v.departure_time : null }); toast.success('Arrivo salvato'); onSaved(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className={`grid grid-cols-1 ${first && v.flight_no ? 'sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto]' : 'sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]'} gap-2 items-start mt-2`} data-testid={`arrival-info-form-${s.id}`}>
      <ArrivalFields prefix={`arrival-${s.id}`} v={v} onChange={setV} day={s.start} className={field} departure={first} />
      <button type="submit" className={`${goldBtn} h-10 px-4 text-sm sm:mt-[17px]`} data-testid={`arrival-info-save-${s.id}`}>Salva</button>
    </form>
  );
}

function ArrivalBlock({ trip, s, n, onSaved }) {
  const [edit, setEdit] = useState(false);
  const a = s.arrival;
  const tz = s.place?.timezone;
  const confirm = async () => {
    try { await api.post(`/travel/trips/${trip.id}/stages/${s.id}/arrival`); toast.success('Arrivo confermato'); onSaved(); } catch (err) { toast.error(errMsg(err)); }
  };
  if (!s.arrival_time) {
    return (
      <div className="mt-2" data-testid={`arrival-missing-${n}`}>
        <p className="text-xs text-[#E8913A] flex items-center gap-2"><AlertTriangle size={13} />Indica l'ora di arrivo (e il volo) per attivare la conferma di arrivo.</p>
        <ArrivalInfoForm trip={trip} s={s} first={n === 1} onSaved={onSaved} />
      </div>
    );
  }
  const [label, col] = STATE[a?.state] || ['Conferma attiva dal giorno prima', '#8A8F97'];
  return (
    <div className="mt-2 text-xs" data-testid={`arrival-block-${n}`}>
      <p className="text-[#C9CCD1]">Arrivo previsto {a ? hhmm(a.expected_at, tz) : `${s.start.slice(8, 10)}/${s.start.slice(5, 7)}, ${s.arrival_time}`} (ora locale)
        {s.flight_no && <> · volo {s.flight_no}{s.airline ? ` · ${s.airline}` : ''}{s.departure_time ? <span data-testid={`arrival-departure-${n}`}> · partenza {s.departure_time} (ora italiana)</span> : null}</>}
        {a?.delay_min ? <span className="text-[#E8913A]"> · ritardo {a.delay_min} min</span> : null}
        {a?.flight_status && <span className="text-[#7A7F87]" data-testid={`arrival-flight-status-${n}`}> · {a.flight_status.status}{a.flight_status.position?.dist_km != null ? ` a ${a.flight_status.position.dist_km} km` : ''} (FlyItalyADSB)</span>}
        <button onClick={() => setEdit(!edit)} className="text-[#D4AF37] hover:underline ml-2" data-testid={`arrival-edit-${n}`}>Modifica</button></p>
      <p className="mt-1 flex items-center gap-2 flex-wrap"><span className="font-semibold" style={{ color: col }} data-testid={`arrival-state-${n}`}>{label}</span>
        {a && !a.confirmed_at && <span className="text-[#7A7F87]">entro {hhmm(a.due_at, tz)}</span>}
        {a?.confirmed_at && <span className="text-[#7A7F87]">{hhmm(a.confirmed_at, tz)} · {a.confirmed_by}</span>}
        {!a?.confirmed_at && <button onClick={confirm} className="text-[#D4AF37] hover:underline" data-testid={`arrival-company-confirm-${n}`}>Conferma per il viaggiatore</button>}</p>
      {edit && <ArrivalInfoForm trip={trip} s={s} first={n === 1} onSaved={() => { setEdit(false); onSaved(); }} />}
    </div>
  );
}

const COL = { red: '#E5484D', orange: '#E8913A', green: '#2E9E6A' };

function EventList({ evs, empty, testid }) {
  if (!evs.length) return <p className="text-xs text-[#5FC18F]" data-testid={`${testid}-empty`}>{empty}</p>;
  return (
    <ul className="space-y-2" data-testid={testid}>
      {evs.slice(0, 12).map((e) => (
        <li key={e.id} className="text-xs border-l-2 pl-2" style={{ borderColor: COL[e.alert] }} data-testid={`trip-event-${e.id}`}>
          <b className="text-[#F2F2F2]">{e.type_label}</b> <span className="text-[#C9CCD1]">· {e.title}</span>
          {e.detail && <span className="block text-[#7A7F87] truncate">{e.detail}</span>}
          <span className="block text-[#5A5F67]">{e.expected ? 'Atteso' : 'In corso'}{e.from ? ` · dal ${e.from.slice(0, 10)}` : ''} · {e.source}</span>
        </li>
      ))}
    </ul>
  );
}

function CityForm({ trip, stage, onSaved }) {
  const [city, setCity] = useState(stage.city || '');
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.put(`/travel/trips/${trip.id}/city`, { city, stage_id: stage.id }); toast.success('Città salvata'); onSaved(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="flex gap-2 mt-2" data-testid={`trip-city-form-${stage.id}`}>
      <CityInput iso3={stage.iso3} value={city} onChange={setCity} className={field} testid={`trip-city-edit-${stage.id}`} />
      <button type="submit" disabled={busy} className={`${goldBtn} h-10 px-4 text-sm shrink-0`} data-testid={`trip-city-save-${stage.id}`}>Salva</button>
    </form>
  );
}

function StageBlock({ trip, s, n, multi, onSaved }) {
  const ev = s.events || { events: [] };
  const [edit, setEdit] = useState(false);
  const near = ev.events.filter((e) => e.near);
  const rest = ev.events.filter((e) => !e.near);
  const remove = async () => {
    if (!window.confirm(`Eliminare la tappa ${n}?`)) return;
    try { await api.delete(`/travel/trips/${trip.id}/stages/${s.id}`); toast.success('Tappa eliminata'); onSaved(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className={multi ? 'border-t border-[#1F242C] pt-4 mt-4 first:border-0 first:pt-0 first:mt-0' : ''} data-testid={`trip-stage-${n}`}>
      {multi && <p className="text-[11px] uppercase tracking-[0.14em] text-[#D4AF37] mb-1">Tappa {n} · {s.start} → {s.end}</p>}
      {s.city ? (
        <p className="text-sm text-[#F2F2F2] flex items-center gap-2" data-testid={`trip-city-label-${n}`}><MapPin size={14} className="text-[#D4AF37] shrink-0" /><span className="truncate">{s.city}, {s.country}</span>
          <button onClick={() => setEdit(!edit)} className="text-xs text-[#D4AF37] hover:underline ml-auto" data-testid={`trip-city-change-${n}`}>Cambia</button>
          {multi && <button onClick={remove} className="text-[#8A8F97] hover:text-[#E37A72]" aria-label="Elimina tappa" data-testid={`trip-stage-delete-${n}`}><Trash2 size={13} /></button>}</p>
      ) : <p className="text-xs text-[#E8913A] flex items-center gap-2" data-testid={`trip-city-missing-${n}`}><AlertTriangle size={13} />Indica la città di destinazione ({s.country}) per vedere gli eventi della zona.</p>}
      <p className="text-[11px] text-[#7A7F87] mt-1">Farnesina: {s.farnesina?.label || 'n.d.'}</p>
      <ArrivalBlock trip={trip} s={s} n={n} onSaved={onSaved} />
      {(!s.city || edit) && <CityForm trip={trip} stage={s} onSaved={() => { setEdit(false); onSaved(); }} />}
      {s.city && (
        <div className="mt-3">
          <p className="text-[11px] uppercase tracking-[0.14em] text-[#8A8F97] mb-2">Nella zona · entro {ev.radius_km} km</p>
          <EventList evs={near} empty={`Nessun evento grave in corso o atteso a ${s.city}.`} testid={`trip-events-near-${n}`} />
        </div>
      )}
      <div className="mt-3">
        <p className="text-[11px] uppercase tracking-[0.14em] text-[#8A8F97] mb-2">{s.city ? 'Nel resto del Paese' : 'Nel Paese'}</p>
        <EventList evs={rest} empty="Nessun evento grave segnalato nel Paese." testid={`trip-events-country-${n}`} />
      </div>
      {!ev.weather_covered && <p className="text-[11px] text-[#5A5F67] mt-2">Allerte meteo ufficiali non disponibili per questo Paese.</p>}
    </div>
  );
}

function AddStage({ trip, onSaved }) {
  const [open, setOpen] = useState(false);
  const [countries, setCountries] = useState([]);
  const [s, setS] = useState({ ...emptyStage(), start: trip.end });
  useEffect(() => { if (open && !countries.length) { api.get('/travel/countries').then((r) => setCountries(r.data)).catch(() => {}); } }, [open, countries.length]);
  const save = async (e) => {
    e.preventDefault();
    try { await api.post(`/travel/trips/${trip.id}/stages`, s); toast.success('Tappa aggiunta'); setOpen(false); setS({ ...emptyStage(), start: s.end }); onSaved(); } catch (err) { toast.error(errMsg(err)); }
  };
  if (!open) return <button onClick={() => setOpen(true)} className={`${ghostBtn} h-9 px-3 text-xs inline-flex items-center gap-2 mt-4`} data-testid="trip-stage-add-open"><Plus size={13} />Aggiungi tappa</button>;
  return (
    <form onSubmit={save} className="mt-4 space-y-2 border-t border-[#1F242C] pt-3" data-testid="trip-stage-add-form">
      <StageFields n={(trip.stages || []).length + 1} s={s} countries={countries} onChange={setS} onRemove={() => setOpen(false)} />
      <button type="submit" className={`${goldBtn} h-9 px-4 text-xs`} data-testid="trip-stage-add-save">Salva tappa</button>
    </form>
  );
}

export default function TripEvents({ trip, onSaved }) {
  const stages = trip.stages || [];
  return (
    <Card className="p-6 min-w-0" data-testid="trip-events">
      <p className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-3">{stages.length > 1 ? `Itinerario · ${stages.length} tappe` : 'Eventi e allerte meteo'}</p>
      {stages.map((s, i) => <StageBlock key={s.id} trip={trip} s={s} n={i + 1} multi={stages.length > 1} onSaved={onSaved} />)}
      {stages.length < 10 && <AddStage trip={trip} onSaved={onSaved} />}
      <p className="text-[11px] text-[#5A5F67] mt-3" data-testid="trip-brief-note">Il viaggiatore riceve via email un riepilogo dei rischi alle 18 del giorno prima di ogni tappa (ora locale del luogo in cui si trova), con il pulsante per confermare l'arrivo entro 3 ore dall'orario previsto: promemoria 1 ora prima della scadenza, poi avviso ai contatti alert dell'azienda.</p>
    </Card>
  );
}
